/**
 * Organization (tenant) state for the org switcher and tenant-first routing.
 *
 * Modelled on `state/connections.ts`: one warm, cached atom fetches the T3
 * server's `/api/amoreg/orgs` endpoint — the orgs the signed-in user may act
 * in (every org for global admins and bootstrap-paired admin sessions) plus
 * viewer identity. Components read it synchronously through `useOrgs`.
 *
 * The active org is the first URL segment (`/$org/...`, slug or id — see
 * `orgRouting.ts`), resolved against this snapshot by `useActiveOrg`.
 * Switching tenants persists the choice both
 * server-side (`User.lastChosenOrgId`, when the session has a platform user)
 * and locally, so `/` can land on the last-picked org before the network
 * answers — and even when the session has no platform user to persist for.
 *
 * @module state/orgs
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";
import { useParams } from "@tanstack/react-router";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { resolveOrgIdFromSegment, setOrgDirectory } from "../orgRouting";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { resolveActiveOrgSegment } from "../threadRoutes";

const OrgSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  slug: Schema.String,
  logoUrl: Schema.NullOr(Schema.String),
  /** Dark-scheme mark; absent on servers that predate it. */
  logoDarkUrl: Schema.optionalKey(Schema.NullOr(Schema.String)),
  role: Schema.NullOr(Schema.String),
  lastChosen: Schema.Boolean,
});
export type OrgView = typeof OrgSchema.Type;

const ViewerSchema = Schema.Struct({
  userId: Schema.NullOr(Schema.String),
  email: Schema.NullOr(Schema.String),
  isGlobalAdmin: Schema.Boolean,
});
export type OrgsViewer = typeof ViewerSchema.Type;

/**
 * An organization this server does not serve because a dedicated deployment
 * does; the switcher links to it there rather than hiding it.
 */
const ElsewhereOrgSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  slug: Schema.String,
  logoUrl: Schema.NullOr(Schema.String),
  logoDarkUrl: Schema.optionalKey(Schema.NullOr(Schema.String)),
  url: Schema.String,
});
export type ElsewhereOrgView = typeof ElsewhereOrgSchema.Type;

const SnapshotSchema = Schema.Struct({
  orgs: Schema.Array(OrgSchema),
  viewer: ViewerSchema,
  /** Absent on servers that serve every organization they list. */
  elsewhere: Schema.optionalKey(Schema.Array(ElsewhereOrgSchema)),
});
type Snapshot = typeof SnapshotSchema.Type;

/** No tenant directory on this server (endpoint 404 — plain T3 dev). */
const UNAVAILABLE: Snapshot = {
  orgs: [],
  viewer: { userId: null, email: null, isGlobalAdmin: false },
};

const decodeSnapshot = Schema.decodeUnknownEffect(SnapshotSchema);

class OrgsRequestError extends Schema.TaggedErrorClass<OrgsRequestError>()("OrgsRequestError", {
  status: Schema.Number,
}) {}

const fetchSnapshot = Effect.gen(function* () {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/orgs");
  const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
  if (response.status === 404) {
    // No tenant directory on this server: drop any previous server's
    // mapping so its slugs cannot translate this server's project ids.
    setOrgDirectory([]);
    return UNAVAILABLE;
  }
  if (!response.ok) {
    return yield* Effect.fail(new OrgsRequestError({ status: response.status }));
  }
  const json = yield* Effect.tryPromise(() => response.json());
  const snapshot = yield* decodeSnapshot(json);
  // Publish the id↔slug directory so route builders render slug URLs.
  setOrgDirectory(snapshot.orgs.map((org) => ({ id: org.id, slug: org.slug })));
  return snapshot;
});

const orgsRuntime = Atom.runtime(Layer.empty);
const orgsAtom = orgsRuntime.atom(fetchSnapshot).pipe(Atom.withLabel("web-amoreg:orgs"));

/**
 * Re-read the directory for the current session. The atom is warm and
 * shared, so a read made while signed out (a 401 cached by whichever
 * component asked first) would otherwise be what the workspace sees right
 * after signing in.
 */
export function refreshOrgs(): void {
  appAtomRegistry.refresh(orgsAtom);
}

/** localStorage fallback for the last-picked org (bootstrap sessions, cold loads). */
export const LAST_PICKED_ORG_KEY = "amoreg:last-picked-org:v1";

export function readLastPickedOrgId(): string | null {
  try {
    return window.localStorage.getItem(LAST_PICKED_ORG_KEY);
  } catch {
    return null;
  }
}

function writeLastPickedOrgId(orgId: string): void {
  try {
    window.localStorage.setItem(LAST_PICKED_ORG_KEY, orgId);
  } catch {
    // Storage unavailable (private mode) — the server-side record still works.
  }
}

/** Record a visited org locally only — used on landing, without a DB write. */
export function recordVisitedOrg(orgId: string): void {
  writeLastPickedOrgId(orgId);
}

/**
 * Rename an organization (org admins and global admins only), then refresh
 * the shared snapshot so the switcher and settings agree. Resolves to an
 * error message for the caller to show, or null on success.
 */
export async function renameOrganization(orgId: string, name: string): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/orgs/name");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ organizationId: orgId, name }),
    });
    if (!response.ok) {
      return response.status === 403
        ? "Only organization admins can rename it."
        : (await response.text().catch(() => "")) || "The organization could not be renamed.";
    }
    appAtomRegistry.refresh(orgsAtom);
    return null;
  } catch {
    return "The organization could not be renamed.";
  }
}

/**
 * Set or clear an organization mark (org admins and global admins only),
 * then refresh the shared snapshot. Resolves to an error message, or null.
 */
export async function updateOrganizationLogo(
  orgId: string,
  mode: "light" | "dark",
  logo: string | null,
): Promise<string | null> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/orgs/logo");
  try {
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ organizationId: orgId, mode, logo }),
    });
    if (!response.ok) {
      return response.status === 403
        ? "Only organization admins can change the logo."
        : (await response.text().catch(() => "")) || "The logo could not be saved.";
    }
    appAtomRegistry.refresh(orgsAtom);
    return null;
  } catch {
    return "The logo could not be saved.";
  }
}

/** Record the picked org locally and, when the session has a user, in the DB. */
export function persistLastPickedOrg(orgId: string): void {
  writeLastPickedOrgId(orgId);
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/orgs/last-chosen");
  void fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ organizationId: orgId }),
  }).catch(() => {
    // Best-effort: the local record already covers the next visit.
  });
}

/**
 * Pick the org `/` should land on: the DB's last-chosen org, then the local
 * record, then the first org. Null while the list is empty.
 */
export function resolveDefaultOrg(orgs: readonly OrgView[]): OrgView | null {
  if (orgs.length === 0) {
    return null;
  }
  const lastChosen = orgs.find((org) => org.lastChosen);
  if (lastChosen) {
    return lastChosen;
  }
  const localId = readLastPickedOrgId();
  const local = localId ? orgs.find((org) => org.id === localId) : undefined;
  return local ?? orgs[0] ?? null;
}

export interface OrgsView {
  readonly orgs: readonly OrgView[];
  /** Organizations served by another deployment, reached by full navigation. */
  readonly elsewhere: readonly ElsewhereOrgView[];
  readonly viewer: OrgsViewer;
  /** False when the server has no tenant directory (plain T3 dev). */
  readonly available: boolean;
  /** True until the first response arrives. */
  readonly isPending: boolean;
  readonly error: string | null;
  readonly refresh: () => void;
}

export function useOrgs(): OrgsView {
  const result = useAtomValue(orgsAtom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(orgsAtom);
  }, []);

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    const elsewhere = (snapshot?.elsewhere ?? []).filter((org) => isHttpsUrl(org.url));
    return {
      orgs: snapshot?.orgs ?? [],
      elsewhere,
      viewer: snapshot?.viewer ?? UNAVAILABLE.viewer,
      // A member whose only organization is served elsewhere still gets a
      // switcher, holding just the link.
      available: snapshot !== null && (snapshot.orgs.length > 0 || elsewhere.length > 0),
      isPending: result.waiting && snapshot === null,
      error: result._tag === "Failure" ? "Organizations could not be loaded." : null,
      refresh,
    };
  }, [result, refresh]);
}

/**
 * The active org id: the URL's first segment on org routes (a slug or an id,
 * resolved against the orgs snapshot), falling back to the last-visited org
 * on org-less routes (`/connections`, and the legacy redirectors) — so the
 * sidebar stays scoped and the switcher keeps its label while the user is
 * off in a static surface.
 */
export function useActiveOrgId(): string | null {
  const routeOrgSegment = useParams({
    strict: false,
    select: (params) => resolveActiveOrgSegment(params as { org?: string }),
  });
  const { orgs } = useOrgs();
  return useMemo(() => {
    if (routeOrgSegment === null) {
      return readLastPickedOrgId();
    }
    // Always resolve against this render's snapshot — never the module
    // directory, which may still hold a previous server's mapping. An empty
    // snapshot passes the segment through, which is correct both before the
    // snapshot loads and on plain T3 servers where the segment *is* the
    // project id.
    return resolveOrgIdFromSegment(
      routeOrgSegment,
      orgs.map((org) => ({ id: org.id, slug: org.slug })),
    );
  }, [orgs, routeOrgSegment]);
}

/** The active org's directory entry, once both the URL and snapshot agree. */
export function useActiveOrg(): OrgView | null {
  const activeOrgId = useActiveOrgId();
  const { orgs } = useOrgs();
  return useMemo(
    () => (activeOrgId ? (orgs.find((org) => org.id === activeOrgId) ?? null) : null),
    [activeOrgId, orgs],
  );
}

/** Only https links leave the app from the switcher. */
export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}
